import {
  SubmitPayOverTimeLoanDisbursementResult,
  SubmitPayOverTimeLoanDisbursementResultTypenames,
} from '@varo/graphql-schema-types';
import {
  buildErrorModel,
  type ErrorModel,
  logPayOverTimeError,
  resolveErrorCopy,
} from 'features/payOverTime/helpers/PayOverTime.helpers';
import { useCallback, useState } from 'react';
import SubmitPayOverTimeLoanDisbursement from 'services/graphql/mutations/SubmitPayOverTimeLoanDisbursement';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import { getUuid } from 'shared/utils/getUuid/getUuid';

const useSubmitPayOverTimeLoanDisbursement = () => {
  const { defaultMessage, defaultTitle } = useDefaultErrorStrings();
  const [{ data, fetching, error }, executeMutation] = useCancellableMutation<{
    submitPayOverTimeLoanDisbursement: SubmitPayOverTimeLoanDisbursementResult;
  }>(SubmitPayOverTimeLoanDisbursement);
  const [localErrorModel, setLocalErrorModel] = useState<ErrorModel | null>(
    null,
  );

  const networkErrorModel: ErrorModel | null = error
    ? buildErrorModel(defaultTitle, defaultMessage)
    : null;

  const resultVariant = data?.submitPayOverTimeLoanDisbursement;

  const successVariant =
    resultVariant?.__typename ===
    SubmitPayOverTimeLoanDisbursementResultTypenames.SubmitPayOverTimeLoanDisbursementSuccess
      ? resultVariant
      : null;

  const graphqlErrorVariant: ErrorModel | null =
    resultVariant?.__typename ===
    SubmitPayOverTimeLoanDisbursementResultTypenames.SubmitPayOverTimeLoanDisbursementError
      ? buildErrorModel(
          resolveErrorCopy(resultVariant.title, defaultTitle),
          resolveErrorCopy(resultVariant.message, defaultMessage),
        )
      : null;

  const submitLoanDisbursement = useCallback(
    async (input: {
      transactionId: string;
      registrationInfoId: string;
      agreementId: string;
    }) => {
      const idempotencyId = getUuid();
      const variables = {
        idempotencyId,
        input: {
          transactionId: input.transactionId,
          registrationInfoId: input.registrationInfoId,
          agreementId: input.agreementId,
        },
      };

      setLocalErrorModel(null);
      try {
        const response = await executeMutation(variables);
        return response.data?.submitPayOverTimeLoanDisbursement ?? null;
      } catch (caught) {
        if (caught instanceof AbortError) {
          return null;
        }

        logPayOverTimeError(caught);

        setLocalErrorModel(buildErrorModel(defaultTitle, defaultMessage));
        return null;
      }
    },
    [executeMutation, defaultMessage, defaultTitle],
  );

  const errorModel =
    networkErrorModel || graphqlErrorVariant || localErrorModel;

  return {
    submitLoanDisbursement,
    fetching,
    data: successVariant,
    errorModel,
  };
};

export default useSubmitPayOverTimeLoanDisbursement;
